import styled from "@emotion/styled";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import MDBox from "components/MDBox";
import ContainerWrapper from "components/Wrapper/ContainerWrapper";
import { useAppContextController } from "context/AppContext";
import { useOutsideCompanyCache } from "hooks/useCompanyCache";
import { getCompanyImageUrl } from "utils/helpers/upload";
// import ExcelDropzone from "@/components/ui/ExcelDropzone";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useSearchParams } from "react-router-dom";
import { confirmInterviewSuggestion } from "./actions/confirmInterviewSuggestion";
import { fetchInterviewSuggestionInformation } from "./actions/fetchInterviewSuggestionInformation";

const useStyles = makeStyles({
  box: {
    marginTop: 20,
    overflow: "visible",
    width: "85%",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100px",
    border: "1px dashed #C9C9C9",
    padding: "20px 20px",
    cursor: "pointer",
    borderRadius: "4px",
  },
  section_image_container: {
    display: "inline-block",
    position: "relative",
    margin: "4px 8px",
    cursor: "pointer",
  },
});

const StyledImg = styled("img")(() => ({
  borderRadius: "15px",
}));

const ScheduleInterviewPage = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const { company, setCompany, setCompanyType, isMobile, currentLoggedUser } =
    useAppContextController();
  const { isLoadingCompany } = useOutsideCompanyCache({
    company,
    setCompany,
    setCompanyType,
    outsideMode: "public",
  });

  const IMAGE_SERVER = getCompanyImageUrl(company);

  const [searchParams, setSearchParams] = useSearchParams();
  const [interviewCreated, setInterviewCreated] = useState(false);

  const backListUrl = searchParams.get("backToList");

  const jobSlugParam = searchParams.get("jobSlug");
  const applicantIdParam = searchParams.get("applicantId");
  const userIdParam = searchParams.get("userId");
  const dateParam = searchParams.get("date");
  const codeParam = searchParams.get("code");

  const outsideMode = currentLoggedUser?._id ? "" : "public";

  const options = {
    jobSlug: jobSlugParam,
    applicantId: applicantIdParam,
    userId: userIdParam,
    date: dateParam,
    code: codeParam,
    outsideMode,
  };

  const { data: interviewInformation, isLoading } = useQuery(
    ["interviewInformation", options],
    () => fetchInterviewSuggestionInformation(options)
  );

  const confirmInterviewSuggestionMutation = useMutation(confirmInterviewSuggestion, {
    onSuccess: () => {
      setInterviewCreated(true);
      enqueueSnackbar("Interview created successfully", { variant: "success" });
    },
    onError: () => {
      enqueueSnackbar("An error has occurred creating the interview", { variant: "error" });
    },
  });

  const error = interviewInformation?.message || "The interview information could not be retrieved";

  const dateString = useMemo(
    () => moment(interviewInformation?.result?.timeString).format("ddd MM/DD hh:mm A"),
    [interviewInformation?.result?.timeString]
  );

  const renderContent = () => {
    // Show loading state first
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      );
    }

    if (!interviewInformation?.success) {
      return (
        <Alert severity="error" sx={{ my: 3 }}>
          {error}
        </Alert>
      );
    }

    return (
      <Container maxWidth="sm" sx={{ py: 4, px: { xs: 2, sm: 3 } }}>
        <Paper
          elevation={3}
          sx={{
            overflow: "visible",
            position: "relative",
            borderRadius: 4,
            mt: 4,
            mb: 4,
            pt: 8,
            pb: 6,
            px: { xs: 3, sm: 6 },
            background: "linear-gradient(to bottom, #ffffff, #f9fafc)",
            textAlign: "center",
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {interviewCreated ? (
            <>
              <Typography variant="h4" sx={{ mb: 2, color: "#1e293b", fontWeight: 700 }}>
                Interview Created
              </Typography>
              <Typography variant="body1" color="text.secondary">
                You can close this window now
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h4" sx={{ mb: 2, color: "#1e293b", fontWeight: 700 }}>
                Confirm Interview Scheduling
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please confirm you want to schedule an interview with the following information
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mt: 2,
                  color: "text.secondary",
                  fontSize: "1rem",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    color: "#1e293b",
                    letterSpacing: "-0.01em",
                    mb: 0.5,
                  }}
                >
                  Applicant:
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 500,
                    color: "info.main",
                    ml: 1,
                  }}
                >
                  {interviewInformation?.result?.applicantName}
                </Typography>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  fontSize: "1rem",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    color: "#1e293b",
                    letterSpacing: "-0.01em",
                    mb: 0.5,
                  }}
                >
                  Job:
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 500,
                    color: "info.main",
                    ml: 1,
                  }}
                >
                  {interviewInformation?.result?.jobName}
                </Typography>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  fontSize: "1rem",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    color: "#1e293b",
                    letterSpacing: "-0.01em",
                    mb: 0.5,
                  }}
                >
                  Company:
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 500,
                    color: "info.main",
                    ml: 1,
                  }}
                >
                  {interviewInformation?.result?.companyName}
                </Typography>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  fontSize: "1rem",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    color: "#1e293b",
                    letterSpacing: "-0.01em",
                    mb: 0.5,
                  }}
                >
                  Date:
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 500,
                    color: "info.main",
                    ml: 1,
                  }}
                >
                  {dateString}
                </Typography>
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
                <Button
                  variant="contained"
                  onClick={() => confirmInterviewSuggestionMutation.mutateAsync(options)}
                  sx={{
                    backgroundColor: "#00A3FF",
                    "&:hover": {
                      backgroundColor: "#0091EA",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#E0E0E0",
                    },
                  }}
                >
                  Confirm
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Container>
    );
  };

  return (
    <>
      <ContainerWrapper sx={{ px: "20px", pt: "15px" }} position="relative">
        <Box position="relative">
          <a href="/">
            <StyledImg
              src={`${getCompanyImageUrl(company)}/company/${company?.slug}/banner/${
                company?.bannerUrl
              }`}
              alt=""
              width="100%"
            />
          </a>
        </Box>
        {renderContent()}
        <MDBox
          sx={{ py: "15px", px: "20px" }}
          bgColor="#222"
          display="flex"
          justifyContent="center"
        >
          <img
            src="/images/png/logos/powered-by-gignology.png"
            alt="powered by gig-nology"
            width="205.8px"
            height="82.8px"
          />
        </MDBox>
      </ContainerWrapper>
    </>
  );
};

export default ScheduleInterviewPage;
